import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Grid,
  Icon,
  Link,
  Text,
  Tooltip,
  useTheme,
} from "@chakra-ui/react";
import React, { useRef } from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { Theme } from "../theme";
import { IconType } from "react-icons";
import { MdArrowForward } from "react-icons/md";

export type NavDataType = {
  name: string;
  img: IconType;
  path: string;
  item: ItemType[];
};
type ItemType = {
  name: string;
  item: SubItemType[];
};
type SubItemType = {
  item_name: string;
  path: string;
};
type NavbarPropType = {
  data: NavDataType[];
  isCMS: boolean;
};

const Navbar = (props: NavbarPropType) => {
  const { data, isCMS } = props;

  let router = useNavigate();
  const theme = useTheme<Theme>();
  const [activeElement, setActiveElement] = React.useState<string>("/");
  const [activeIndex, setActiveIndex] = React.useState<number>();

  React.useEffect(() => {
    if (window.location.pathname === "/") {
      setActiveElement("/");
    } else {
      setActiveElement(window.location.pathname);
    }
  }, [window.location.pathname]);

  const onNavIconClick = (val: string) => {
    setActiveElement(val);
    router(val);
  };

  const getIndexValue = (path: string) => {
    const index = data.map((item, index) => {
      if (item.path.split("/")[1] === path) {
        return index;
      }
    });
    const result = index.filter((val) => val !== undefined);
    return result[0];
  };

  React.useEffect(() => {
    setActiveIndex(getIndexValue(activeElement.split("/")[1]));
  }, [activeElement]);

  return (
    <Grid sx={{ height: "100%" }}>
      <Box sx={{ width: "100%", height: "100%" }}>
        <Flex
          sx={{
            width: "100%",
            height: "100%",
            position: "relative",
          }}
        >
          <Box
            className="navigation"
            sx={{
              backgroundColor: "#FFFFFF",
              width: "100%",
              height: "100%",
              zIndex: 50,
            }}
          >
            <Flex
              sx={{
                height: "5.4rem",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                borderBottom: "1px solid",
                borderColor: theme.colors.divider,
              }}
            >
              <Flex
                sx={{
                  height: "4rem",
                  width: "4rem",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <img
                  src="/logo-icon.png"
                  style={{
                    maxHeight: "100%",
                    maxWidth: "100%",
                    objectFit: "contain",
                  }}
                />
              </Flex>
            </Flex>
            <Box
              sx={{
                height: "calc(100% - 5.4rem)",
                paddingTop: "1rem",
              }}
            >
              <Accordion
                index={activeIndex}
                allowToggle
                style={{ padding: "0 0.5rem" }}
              >
                {data?.map((list, index) => {
                  return (
                    <AccordionItem
                      key={index}
                      style={{ border: "none" }}
                      onClick={() => {
                        setActiveIndex(index);
                      }}
                    >
                      <NavigationLink
                        img={list.img}
                        href={list.path}
                        menuName={list.name}
                        onNavIconClick={onNavIconClick}
                        routerPath={activeElement}
                        isCMS={isCMS}
                      />
                      <AccordionPanel pb={4}>
                        {list.item.map((item, index) => {
                          return (
                            <Box key={index}>
                              {item.item.map((item, index) => {
                                return (
                                  <Flex
                                    key={index}
                                    sx={{
                                      justifyContent: "flex-start",
                                      alignItems: "center",
                                    }}
                                  >
                                    <SubNavigationLink
                                      href={item.path}
                                      subMenuName={item.item_name}
                                      router={router}
                                    />
                                  </Flex>
                                );
                              })}
                            </Box>
                          );
                        })}
                      </AccordionPanel>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </Box>
          </Box>
        </Flex>
      </Box>
    </Grid>
  );
};

export default Navbar;

type NavigationLinkPropType = {
  img: IconType;
  href: string;
  menuName: string;
  onNavIconClick: (val: string) => void;
  routerPath: string;
  isCMS: boolean;
};

function NavigationLink({
  href,
  menuName,
  onNavIconClick,
  img,
  isCMS,
  routerPath,
}: NavigationLinkPropType) {
  const theme = useTheme<Theme>();
  const isActive = routerPath.split("/")[1] === `${href.split("/")[1]}`;

  return (
    <AccordionButton
      onClick={() => {
        onNavIconClick(`${href}`);
      }}
      sx={{
        marginBottom: "0.25rem",
        padding: "0.5rem",
        borderRadius: "0.5rem",
        background: isActive ? theme.colors.primary.main : "transparent",
        cursor: "pointer",
        _hover: isActive
          ? { backgroundColor: theme.colors.primary.dark }
          : { backgroundColor: theme.colors.primary.light },
      }}
    >
      <Flex
        sx={{
          padding: "0 0.5rem",
          alignItems: "center",
          height: "2.8rem",
          borderRadius: "0.25rem",
          gap: "0.5rem",
        }}
      >
        <Icon
          as={img}
          width={6}
          height={6}
          color={isActive ? "#FFFFFF" : theme.colors.primary.main}
          cursor="pointer"
        />
        <Text
          variant={"body"}
          color={isActive ? "#FFFFFF" : theme.colors.primary.main}
        >
          {menuName}
        </Text>
      </Flex>
    </AccordionButton>
  );
}

type SubNavigationLinkPropType = {
  href: string;
  subMenuName: string;
  router: NavigateFunction;
};

function SubNavigationLink({ href, subMenuName }: SubNavigationLinkPropType) {
  const theme = useTheme<Theme>();
  const isActive = window.location.pathname === `/${href}`;
  const navigate = useNavigate();

  const handleButtonClick = () => {
    navigate(href);
  };

  return (
    <Button
      sx={{
        borderRadius: "0.5rem",
        width: "100%",
        padding: "0.25rem",
        height: "2.5rem",
        justifyContent: "flex-start",
        _hover: { backgroundColor: theme.colors.primary.light },
        background: "none",
      }}
      onClick={() => handleButtonClick()}
    >
      <Flex
        sx={{
          alignItems: "center",
          justifyContent: "flex-start",
          height: "2.8rem",
          borderRadius: "0.25rem",
          gap: "0.5rem",
        }}
      >
        {isActive ? (
          <Icon
            as={MdArrowForward}
            width={6}
            height={6}
            color={theme.colors.primary.main}
            cursor="pointer"
          />
        ) : (
          <Box style={{ paddingLeft: "1.5rem" }}></Box>
        )}
        <Text variant={"body"} color={theme.colors.primary.main}>
          {subMenuName}
        </Text>
      </Flex>
    </Button>
  );
}
